// web/src/lib/auth.ts — MSAL (Entra External ID / CIAM) per D14 §1 + D16.
// In mock mode (VITE_USE_MOCK !== '0') auth is BYPASSED: the app renders without any Entra config — that is the
// whole point of see-it-first. Only when VITE_USE_MOCK=0 does login()/token() actually talk to Entra.
import { PublicClientApplication } from '@azure/msal-browser';
import type { Configuration, AccountInfo } from '@azure/msal-browser';

export const USE_MOCK = import.meta.env.VITE_USE_MOCK !== '0';

const SUBDOMAIN = import.meta.env.VITE_ENTRA_SUBDOMAIN ?? '';
const SCOPES = [import.meta.env.VITE_ENTRA_API_SCOPE ?? ''].filter(Boolean);

const config: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID ?? '',
    authority: `https://${SUBDOMAIN}.ciamlogin.com/`,
    redirectUri: import.meta.env.VITE_APP_URL ?? window.location.origin,
    navigateToLoginRequestUrl: false, // D16 §3 — we restore ?next= ourselves in main.tsx
  },
  cache: { cacheLocation: 'localStorage' },
};

// Real MSAL instance (constructed even in mock so MsalProvider has something to wrap; never used while mocking).
export const msal = new PublicClientApplication(config);

let initialized = false;
// MSAL v3 requires initialize() + handleRedirectPromise() before any account/token call.
export async function initAuth(): Promise<void> {
  if (USE_MOCK || initialized) return;
  await msal.initialize();
  await msal.handleRedirectPromise();
  initialized = true;
}

export function isSignedIn(): boolean {
  if (USE_MOCK) return true;
  return msal.getAllAccounts().length > 0;
}

export function account(): AccountInfo | null {
  return msal.getAllAccounts()[0] ?? null;
}

export async function login(): Promise<void> {
  if (USE_MOCK) return;
  await msal.loginRedirect({ scopes: SCOPES });
}

export async function logout(): Promise<void> {
  if (USE_MOCK) { window.location.href = '/'; return; }
  await msal.logoutRedirect();
}

// Bearer access token for the API. Falls back to interactive redirect if the silent acquire fails.
export async function token(): Promise<string> {
  if (USE_MOCK) return 'mock-token';
  const a = account();
  if (!a) { await login(); throw new Error('auth'); }
  try {
    const r = await msal.acquireTokenSilent({ account: a, scopes: SCOPES });
    return r.accessToken;
  } catch {
    await msal.acquireTokenRedirect({ account: a, scopes: SCOPES });
    throw new Error('auth');
  }
}
